// /client/src/components/BatchDataV3Panel.js
//
// Phase 8: UI for the BatchData V3 weak-match recovery workflow.
// - Preflight status (schema / config / API key)
// - County pool summary (weak rows, already processed, credits used)
// - Dry-run preview (free) and guarded live runs (spends credits, max 25/run)
// - Per-owner results with drill-down into persisted V3 contacts

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { scrapingService } from '../services/scrapingService';
import MultiCountySelect from './MultiCountySelect';

const STATUS_STYLES = {
  v3_recovered: 'bg-green-100 text-green-800 border-green-200',
  entity_contacts_preserved: 'bg-blue-100 text-blue-800 border-blue-200',
  v3_property_owner_verified_only: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  unresolved_mismatch: 'bg-orange-100 text-orange-800 border-orange-200',
  no_contact_returned: 'bg-gray-100 text-gray-700 border-gray-200',
  error: 'bg-red-100 text-red-800 border-red-200',
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${cls}`}>
      {status || 'unknown'}
    </span>
  );
}

function StatBox({ label, value, color = 'text-gray-900' }) {
  return (
    <div className="bg-gray-50 rounded-lg border p-3 text-center">
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function OwnerContactsModal({ uniqueId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    scrapingService.fetchBatchDataV3OwnerContacts(uniqueId)
      .then((res) => { if (mounted) setData(res); })
      .catch((err) => { if (mounted) setError(err.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [uniqueId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">V3 contacts — {uniqueId}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {loading && <div className="text-sm text-gray-500 animate-pulse">Loading contacts…</div>}
        {error && <div className="text-sm text-red-600">{error}</div>}

        {data && data.summary && (
          <div className="mb-4 text-sm text-gray-700 flex flex-wrap gap-x-4 gap-y-1">
            <span>Status: <StatusBadge status={data.summary.final_match_status} /></span>
            <span>Best: <strong>{data.summary.final_best_name || '—'}</strong>
              {data.summary.final_name_match_score != null && ` (${data.summary.final_name_match_score})`}</span>
            <span>Credits: {data.summary.v3_credits_used ?? 0}</span>
          </div>
        )}

        {data && (!data.persons || data.persons.length === 0) && (
          <div className="text-sm text-gray-500">No V3 contact persons stored for this owner yet.</div>
        )}

        {data && (data.persons || []).map((p) => (
          <div
            key={p.contact_person_id}
            className={`border rounded-lg p-3 mb-3 ${p.selected_for_final ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}
          >
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <strong className="text-gray-900">{p.full_name}</strong>
              {p.selected_for_final
                ? <span className="text-xs font-medium text-green-700">SELECTED #{p.selection_rank} ({p.contact_role})</span>
                : <span className="text-xs text-gray-500">rejected{p.rejection_reason ? ` — ${p.rejection_reason}` : ''}</span>}
              {p.name_match_score != null && <span className="text-xs text-gray-500">score {p.name_match_score}</span>}
            </div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs text-gray-700">
              <div>
                {(p.emails || []).map((e) => (
                  <div key={e.contact_email_id}>
                    ✉️ {e.email}{e.is_selected_email && <span className="text-green-700 font-medium"> ✓</span>}
                  </div>
                ))}
                {(!p.emails || !p.emails.length) && <div className="text-gray-400">no emails</div>}
              </div>
              <div>
                {(p.phones || []).map((ph) => (
                  <div key={ph.contact_phone_id}>
                    📞 {ph.phone_number} <span className="text-gray-400">({ph.phone_type})</span>
                    {ph.is_selected_phone && <span className="text-green-700 font-medium"> ✓</span>}
                  </div>
                ))}
                {(!p.phones || !p.phones.length) && <div className="text-gray-400">no phones</div>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const BatchDataV3Panel = () => {
  // County selection (self-contained, like other panels)
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState('');
  const [counties, setCounties] = useState([]);
  const [selectedCounties, setSelectedCounties] = useState([]); // single-select: 0 or 1 entries
  const countyId = selectedCounties[0] ? String(selectedCounties[0].county_id) : '';

  // Acres filter (same semantics as the Leads tab)
  const [acresFilterMode, setAcresFilterMode] = useState('between'); // 'atleast' | 'atmax' | 'between'
  const [minAcres, setMinAcres] = useState(50);
  const [maxAcres, setMaxAcres] = useState(''); // '' = unlimited
  const [countyMinAcres, setCountyMinAcres] = useState(null);
  const [countyMaxAcres, setCountyMaxAcres] = useState(null);

  // Preflight + summary
  const [preflight, setPreflight] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  // Run controls
  const [limit, setLimit] = useState(5);
  const [includeEntities, setIncludeEntities] = useState(false);
  const [forceRerun, setForceRerun] = useState(false);
  const [confirmLive, setConfirmLive] = useState(false);
  const [running, setRunning] = useState(false);

  // Results
  const [dryRunResult, setDryRunResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);
  const [contactsFor, setContactsFor] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    scrapingService.fetchBatchDataV3Preflight()
      .then(setPreflight)
      .catch((err) => toast.error(`V3 preflight failed: ${err.message}`));
    scrapingService.fetchStates()
      .then((res) => {
        const list = res.states || res || [];
        setStates(list);
        const texas = list.find((s) => /texas/i.test(s.state_name || s.name || ''));
        if (texas) setStateId(String(texas.state_id ?? texas.id));
      })
      .catch((err) => toast.error(`Failed to load states: ${err.message}`));
  }, []);

  useEffect(() => {
    if (!stateId) return;
    scrapingService.fetchCounties(stateId)
      .then((res) => setCounties(res.counties || res || []))
      .catch((err) => toast.error(`Failed to load counties: ${err.message}`));
  }, [stateId]);

  // County acreage range display (parity with the Leads tab)
  useEffect(() => {
    setCountyMinAcres(null);
    setCountyMaxAcres(null);
    if (!countyId) return;
    let mounted = true;
    scrapingService.fetchAcreageRange(countyId)
      .then((range) => {
        if (!mounted) return;
        setCountyMinAcres(range?.min ?? null);
        setCountyMaxAcres(range?.max ?? null);
      })
      .catch(() => { /* non-blocking: range display is informational only */ });
    return () => { mounted = false; };
  }, [countyId]);

  const refreshSummary = useCallback(async (cid) => {
    const id = cid || countyId;
    if (!id) return;
    setLoadingSummary(true);
    try {
      const res = await scrapingService.fetchBatchDataV3Summary(id);
      setSummary(res);
    } catch (err) {
      toast.error(`Failed to load V3 summary: ${err.message}`);
    } finally {
      setLoadingSummary(false);
    }
  }, [countyId]);

  useEffect(() => {
    setSummary(null);
    setMetrics(null);
    setDryRunResult(null);
    setLiveResult(null);
    if (countyId) refreshSummary(countyId);
  }, [countyId, refreshSummary]);

  const parsedLimit = limit === '' ? null : Math.max(parseInt(limit, 10) || 1, 1);
  // Phase 3: no more 25 cap — blank limit = process ALL matching targets
  // (batched 100 owners per BatchData API call server-side).

  // Effective acres filter values (0 / blank = no filter, so default behavior is unchanged)
  const parsedMin = minAcres === '' ? null : Number(minAcres);
  const parsedMax = maxAcres === '' ? null : Number(maxAcres);
  const effectiveMinAcres = acresFilterMode === 'atmax' ? null : (parsedMin > 0 ? parsedMin : null);
  const effectiveMaxAcres = acresFilterMode === 'atleast' ? null : (parsedMax > 0 ? parsedMax : null);
  const acresFilterLabel = (() => {
    if (effectiveMinAcres == null && effectiveMaxAcres == null) return 'No acres filter';
    if (effectiveMinAcres != null && effectiveMaxAcres != null) return `Acres ${effectiveMinAcres}–${effectiveMaxAcres}`;
    if (effectiveMinAcres != null) return `Acres ≥ ${effectiveMinAcres}`;
    return `Acres ≤ ${effectiveMaxAcres}`;
  })();

  const acresPayload = {
    filterMode: acresFilterMode,
    minAcres: effectiveMinAcres,
    maxAcres: effectiveMaxAcres,
  };

  const runDryRun = async () => {
    if (!countyId) return toast.error('Select a county first');
    setRunning(true);
    setDryRunResult(null);
    try {
      const res = await scrapingService.runBatchDataV3WeakMatchRecovery({
        countyId: parseInt(countyId, 10),
        limit: parsedLimit, // null = preview the full filtered pool
        dryRun: true,
        mock: true,
        includeEntities,
        excludeProcessed: !forceRerun,
        ...acresPayload,
      });
      setDryRunResult(res);
      const total = res.estimates?.totalTargets ?? res.sampleRows?.length ?? 0;
      toast.success(`Dry-run preview: ${total} targets (no credits spent)`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRunning(false);
    }
  };

  const runLive = async () => {
    if (!countyId) return toast.error('Select a county first');
    if (!confirmLive) return toast.error('Tick the confirmation checkbox first — live runs spend real credits.');
    if (parsedLimit === null && !dryRunResult) {
      return toast.error('Run a dry-run preview first to see the estimated cost of a full run.');
    }
    setRunning(true);
    setLiveResult(null);
    try {
      const res = await scrapingService.runBatchDataV3WeakMatchRecovery({
        countyId: parseInt(countyId, 10),
        limit: parsedLimit, // null = process ALL matching targets
        dryRun: false,
        mock: false,
        confirmLive: true,
        includeEntities,
        excludeProcessed: !forceRerun,
        ...acresPayload,
      });
      setLiveResult(res);
      toast.success(`Live run done: ${res.counts?.recovered ?? 0} recovered, ${res.counts?.creditsUsed ?? 0} credits used`);
      refreshSummary();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRunning(false);
      setConfirmLive(false);
    }
  };

  const preflightOk = preflight?.ok === true;
  const byFinal = summary?.byFinalStatus || {};

  const loadMetrics = async () => {
    if (!countyId) return toast.error('Select a county first');
    setLoadingMetrics(true);
    try {
      const res = await scrapingService.fetchBatchDataV3RecoveryMetrics({
        countyId: parseInt(countyId, 10),
        filterMode: acresFilterMode,
        minAcres: effectiveMinAcres,
        maxAcres: effectiveMaxAcres,
      });
      setMetrics(res);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const exportCsv = async (shape) => {
    if (!countyId) return toast.error('Select a county first');
    setExporting(true);
    try {
      await scrapingService.downloadBatchDataV3FinalCsv(parseInt(countyId, 10), shape, {
        filterMode: acresFilterMode,
        minAcres: effectiveMinAcres,
        maxAcres: effectiveMaxAcres,
      });
      const label = shape === 'contact' ? 'Contacts' : shape === 'leads' ? 'Leads' : 'Owners';
      toast.success(`${label} CSV downloaded`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header + preflight */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-6 py-4 text-white">
        <h2 className="text-lg font-semibold">BatchData V3 — Weak-Match Recovery</h2>
        <p className="text-sm text-blue-100 mt-1">
          Re-trace owners whose enriched contact name doesn't match the CAD owner name.
        </p>
        {preflight && (
          <div className="mt-2 flex flex-wrap gap-3 text-xs">
            <span className={preflight.schema?.ok ? 'text-green-200' : 'text-red-200'}>
              Schema: {preflight.schema?.ok ? 'OK' : 'MISSING COLUMNS'}
            </span>
            <span className={preflight.apiKeyConfigured ? 'text-green-200' : 'text-red-200'}>
              API key: {preflight.apiKeyConfigured ? `OK (${preflight.apiKeyEnv})` : 'NOT CONFIGURED'}
            </span>
            {preflight.config && (
              <span className="text-blue-100">
                Thresholds: individual {preflight.config.individualThreshold} / spouse {preflight.config.spouseThreshold}
              </span>
            )}
          </div>
        )}
      </div>

      {/* County + controls */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <select
              value={stateId}
              onChange={(e) => { setStateId(e.target.value); setSelectedCounties([]); }}
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="">Select state…</option>
              {states.map((s) => (
                <option key={s.state_id ?? s.id} value={s.state_id ?? s.id}>{s.state_name || s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
            <MultiCountySelect
              single
              counties={counties}
              selectedCounties={selectedCounties}
              onChange={setSelectedCounties}
              disabled={!counties.length}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Owners per run</label>
            <input
              type="number" min="1" value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder="blank = all"
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
            <p className="text-[11px] text-gray-400 mt-0.5">Blank = ALL matching targets (dry-run first is required).</p>
          </div>
          <div className="flex flex-col justify-end pb-2 gap-1">
            <label className="flex items-center text-sm text-gray-700 gap-2">
              <input
                type="checkbox" checked={includeEntities}
                onChange={(e) => setIncludeEntities(e.target.checked)}
              />
              Include entity owners
            </label>
            <label className="flex items-center text-sm text-gray-700 gap-2" title="Re-run owners already completed by a previous V3 run (spends credits again)">
              <input
                type="checkbox" checked={forceRerun}
                onChange={(e) => setForceRerun(e.target.checked)}
              />
              Re-run already processed
            </label>
          </div>
        </div>

        {/* Acres filter (same semantics as the Leads tab) */}
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Acres Filter</label>
            <select
              value={acresFilterMode}
              onChange={(e) => setAcresFilterMode(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="atleast">At least</option>
              <option value="atmax">At max</option>
              <option value="between">In between</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {acresFilterMode === 'atleast' ? 'Minimum acres'
                : acresFilterMode === 'atmax' ? 'Maximum acres' : 'Min acres'}
            </label>
            <input
              type="number"
              min={0}
              value={acresFilterMode === 'atmax' ? maxAcres : minAcres}
              onChange={(e) => {
                const value = e.target.value === '' ? '' : Number(e.target.value);
                if (acresFilterMode === 'atmax') {
                  setMaxAcres(value);
                } else {
                  setMinAcres(value === '' ? 0 : value);
                }
              }}
              className="w-28 border rounded-md px-3 py-2 text-sm"
            />
          </div>
          {acresFilterMode === 'between' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max acres</label>
              <input
                type="number"
                min={minAcres}
                value={maxAcres}
                onChange={(e) => setMaxAcres(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="unlimited"
                className="w-28 border rounded-md px-3 py-2 text-sm"
              />
            </div>
          )}
          <div className="text-xs text-gray-500 pb-2">
            <span className="font-medium">{acresFilterLabel}</span>
            {countyId && countyMinAcres !== null && countyMaxAcres !== null && (
              <span className="ml-2">
                📏 County range: {Number(countyMinAcres).toLocaleString()}–{Number(countyMaxAcres).toLocaleString()} acres
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={runDryRun}
            disabled={running || !countyId}
            className={`px-4 py-2 rounded-md text-sm font-medium border ${running || !countyId
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}
          >
            {running ? 'Working…' : 'Dry-run preview (free)'}
          </button>

          <div className="flex items-center gap-2">
            <label className="flex items-center text-sm text-gray-700 gap-2">
              <input
                type="checkbox" checked={confirmLive}
                onChange={(e) => setConfirmLive(e.target.checked)}
                disabled={running}
              />
              I understand this spends real credits (~1–3 per owner)
              {dryRunResult?.estimates && parsedLimit === null && (
                <span className="text-blue-700 font-medium">
                  — est. ${dryRunResult.estimates.estimatedCostMinUsd}–${dryRunResult.estimates.estimatedCostMaxUsd}
                </span>
              )}
            </label>
            <button
              onClick={runLive}
              disabled={running || !countyId || !confirmLive || !preflightOk}
              className={`px-4 py-2 rounded-md text-sm font-medium ${running || !countyId || !confirmLive || !preflightOk
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            >
              {running ? 'Running…' : `Run LIVE (${parsedLimit === null ? 'ALL matching' : parsedLimit} owners)`}
            </button>
          </div>
        </div>
        {!preflightOk && preflight && (
          <p className="text-xs text-red-600 mt-2">
            Live runs disabled: fix the preflight issues above (schema/config/API key) first.
          </p>
        )}
      </div>

      {/* County summary */}
      {countyId && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-base font-semibold text-gray-900">County V3 status</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={() => exportCsv('leads')}
                className="text-sm font-medium text-green-700 hover:text-green-900 disabled:text-gray-400"
                disabled={exporting}
                title="Leads-tab column layout + name-match columns; uses the acres filter above"
              >
                {exporting ? 'Exporting…' : 'Export leads CSV'}
              </button>
              <button
                onClick={() => exportCsv('owner')}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                disabled={exporting}
              >
                Export owners CSV
              </button>
              <button
                onClick={() => exportCsv('contact')}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                disabled={exporting}
              >
                Export contacts CSV
              </button>
              <button
                onClick={() => refreshSummary()}
                className="text-sm text-blue-600 hover:text-blue-800"
                disabled={loadingSummary}
              >
                {loadingSummary ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>
          {loadingSummary && !summary && <div className="text-sm text-gray-500 animate-pulse">Loading…</div>}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatBox label="Summary rows" value={(summary.totalSummaryRows ?? 0).toLocaleString()} />
              <StatBox label="Recovered" value={byFinal.v3_recovered || 0} color="text-green-600" />
              <StatBox label="Unresolved" value={byFinal.unresolved_mismatch || 0} color="text-orange-600" />
              <StatBox label="No contact" value={byFinal.no_contact_returned || 0} color="text-gray-600" />
              <StatBox label="Owner-verified only" value={byFinal.v3_property_owner_verified_only || 0} color="text-yellow-600" />
              <StatBox label="Credits used" value={summary.creditsUsed ?? 0} color="text-blue-600" />
            </div>
          )}
        </div>
      )}

      {/* Recovery impact (before/after, acreage-filtered) */}
      {countyId && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Recovery impact</h3>
              <p className="text-xs text-gray-500">
                Before/after comparison using the acres filter above ({acresFilterLabel}).
              </p>
            </div>
            <button
              onClick={loadMetrics}
              className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
              disabled={loadingMetrics}
            >
              {loadingMetrics ? 'Loading…' : (metrics ? 'Refresh metrics' : 'Load metrics')}
            </button>
          </div>

          {metrics && (
            <>
              <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
                <div className="border rounded-lg p-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Before V3 (baseline)</div>
                  <div className="text-sm text-gray-700 space-y-1">
                    <div>Enriched owners in range: <strong>{metrics.baseline.enrichedRows.toLocaleString()}</strong></div>
                    <div>Well-matched (strong): <strong className="text-green-700">{metrics.baseline.strongRows.toLocaleString()}</strong></div>
                    <div>Weak matches: <strong className="text-orange-700">{metrics.baseline.weakRows.toLocaleString()}</strong></div>
                    <div>Entities: <strong className="text-blue-700">{metrics.baseline.entityRows.toLocaleString()}</strong></div>
                    <div className="text-xs text-gray-500 pt-1">
                      Valid emails / phones (strong only): {metrics.baseline.strongWithEmail} / {metrics.baseline.strongWithPhone}
                    </div>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">After V3 (individuals)</div>
                  <div className="text-sm text-gray-700 space-y-1">
                    <div>Weak owners processed: <strong>{metrics.v3.individual.completed.toLocaleString()}</strong>
                      {metrics.comparison.weakProcessedPct != null && ` (${metrics.comparison.weakProcessedPct}%)`}</div>
                    <div>Recovered: <strong className="text-green-700">{metrics.v3.recovered.toLocaleString()}</strong>
                      {metrics.comparison.recoveryRatePct != null && ` (${metrics.comparison.recoveryRatePct}% of weak pool)`}</div>
                    <div className="pt-1 border-t mt-2 text-xs font-medium text-gray-500 uppercase">Valid contacts</div>
                    <div>Emails: <strong>{metrics.comparison.contacts.individuals.emailBefore.toLocaleString()}</strong>
                      {' → '}<strong className="text-green-700">{metrics.comparison.contacts.individuals.emailAfter.toLocaleString()}</strong>
                      <span className="text-xs text-green-600"> (+{metrics.comparison.contacts.individuals.emailGained})</span></div>
                    <div>Phones: <strong>{metrics.comparison.contacts.individuals.phoneBefore.toLocaleString()}</strong>
                      {' → '}<strong className="text-green-700">{metrics.comparison.contacts.individuals.phoneAfter.toLocaleString()}</strong>
                      <span className="text-xs text-green-600"> (+{metrics.comparison.contacts.individuals.phoneGained})</span></div>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Entities</div>
                  <div className="text-sm text-gray-700 space-y-1">
                    <div>Entity owners in range: <strong className="text-blue-700">{metrics.comparison.entities.pool.toLocaleString()}</strong></div>
                    <div>Processed by V3: <strong>{metrics.comparison.entities.processed.toLocaleString()}</strong>
                      {metrics.comparison.entities.processedPct != null && ` (${metrics.comparison.entities.processedPct}%)`}</div>
                    <div>Contact opportunities: <strong className="text-green-700">{metrics.comparison.entities.opportunities.toLocaleString()}</strong>
                      {metrics.comparison.entities.opportunityRatePct != null && ` (${metrics.comparison.entities.opportunityRatePct}% of processed)`}</div>
                    <div>Contacts added: <strong className="text-green-700">+{metrics.comparison.entities.contactsAdded.toLocaleString()}</strong></div>
                    <div className="pt-1 border-t mt-2 text-xs font-medium text-gray-500 uppercase">Valid contacts</div>
                    <div>Emails: <strong>{metrics.comparison.contacts.entities.emailBefore.toLocaleString()}</strong>
                      {' → '}<strong className="text-green-700">{metrics.comparison.contacts.entities.emailAfter.toLocaleString()}</strong>
                      <span className="text-xs text-green-600"> (+{metrics.comparison.contacts.entities.emailGained})</span></div>
                    <div>Phones: <strong>{metrics.comparison.contacts.entities.phoneBefore.toLocaleString()}</strong>
                      {' → '}<strong className="text-green-700">{metrics.comparison.contacts.entities.phoneAfter.toLocaleString()}</strong>
                      <span className="text-xs text-green-600"> (+{metrics.comparison.contacts.entities.phoneGained})</span></div>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Net result &amp; efficiency</div>
                  <div className="text-sm text-gray-700 space-y-1">
                    <div>Well-matched: <strong>{metrics.comparison.wellMatchedBefore.toLocaleString()}</strong>
                      {' → '}<strong className="text-green-700">{metrics.comparison.wellMatchedAfter.toLocaleString()}</strong>
                      <span className="text-xs text-green-600"> (+{metrics.comparison.ownersGained})</span></div>
                    <div>Contacts gained (email + phone): <strong className="text-green-700">+{metrics.comparison.efficiency.contactsGained.toLocaleString()}</strong></div>
                    <div className="pt-1 border-t mt-2">
                      Credits spent (live): <strong className="text-blue-700">{metrics.credits.totalCreditsCharged.toLocaleString()}</strong>
                    </div>
                    <div>Cost: <strong className="text-blue-700">${metrics.credits.totalCostUsd}</strong>
                      <span className="text-xs text-gray-400"> (@ ${metrics.credits.creditCostUsd}/credit)</span></div>
                    {metrics.comparison.efficiency.creditsPerRecovered != null && (
                      <div>Per owner recovered: <strong>{metrics.comparison.efficiency.creditsPerRecovered}</strong> credits
                        {metrics.comparison.efficiency.costPerRecoveredUsd != null && (
                          <span className="text-xs text-gray-500"> (${metrics.comparison.efficiency.costPerRecoveredUsd})</span>
                        )}</div>
                    )}
                    {metrics.comparison.efficiency.creditsPerContactGained != null && (
                      <div>Per contact gained: <strong>{metrics.comparison.efficiency.creditsPerContactGained}</strong> credits
                        {metrics.comparison.efficiency.costPerContactGainedUsd != null && (
                          <span className="text-xs text-gray-500"> (${metrics.comparison.efficiency.costPerContactGainedUsd})</span>
                        )}</div>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-gray-400">
                Baseline from enriched contact columns; V3 results from the recovery summary; credits summed over all live V3 runs
                ({metrics.credits.liveRuns} run{metrics.credits.liveRuns === 1 ? '' : 's'}). "Valid" contact counts are owner-level:
                before = strong matches only (weak-match contact info belongs to a mismatched name); after = before + owners whose V3
                contacts have usable email/phone. Entities are tracked separately from individual match metrics.
              </p>
            </>
          )}
          {!metrics && !loadingMetrics && (
            <p className="text-sm text-gray-400">Click "Load metrics" to compute the before/after comparison (read-only, free).</p>
          )}
        </div>
      )}

      {/* Dry-run preview */}
      {dryRunResult && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-1">Dry-run preview</h3>
          <p className="text-xs text-gray-500 mb-3">
            Pool: {dryRunResult.counts?.weakRows ?? 0} weak rows,
            {' '}{dryRunResult.counts?.alreadyProcessedSkipped ?? 0} already processed (skipped).
            No credits were spent.
          </p>

          {dryRunResult.estimates && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
              <StatBox label="Targets" value={dryRunResult.estimates.totalTargets} />
              <StatBox label="With address" value={dryRunResult.estimates.targetsWithAddress} color="text-green-600" />
              <StatBox label="No address" value={dryRunResult.estimates.targetsWithoutAddress} color="text-gray-500" />
              <StatBox
                label="Est. credits"
                value={`${dryRunResult.estimates.estimatedCreditsMin}–${dryRunResult.estimates.estimatedCreditsMax}`}
                color="text-blue-600"
              />
              <StatBox
                label="Est. cost"
                value={`$${dryRunResult.estimates.estimatedCostMinUsd}–$${dryRunResult.estimates.estimatedCostMaxUsd}`}
                color="text-blue-600"
              />
              <StatBox
                label={`API batches (×${dryRunResult.estimates.batchSize})`}
                value={dryRunResult.estimates.estimatedBatches}
              />
            </div>
          )}

          {dryRunResult.estimates && dryRunResult.estimates.totalTargets > (dryRunResult.sampleRows || []).length && (
            <p className="text-xs text-gray-500 mb-2">
              Showing first {(dryRunResult.sampleRows || []).length} of {dryRunResult.estimates.totalTargets} targets below.
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-4">Owner</th>
                  <th className="py-2 pr-4">CAD name</th>
                  <th className="py-2 pr-4">Current enriched</th>
                  <th className="py-2 pr-4">Score</th>
                  <th className="py-2 pr-4">Address to send</th>
                </tr>
              </thead>
              <tbody>
                {(dryRunResult.sampleRows || []).map((row) => (
                  <tr key={row.unique_id} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-mono text-xs">{row.unique_id}</td>
                    <td className="py-2 pr-4">{row.cad_owner_name}</td>
                    <td className="py-2 pr-4">{row.current?.enriched_full_name || '—'}</td>
                    <td className="py-2 pr-4">{row.current?.cad_name_match_score ?? '—'}</td>
                    <td className="py-2 pr-4 text-xs text-gray-600">
                      {row.requestPreview
                        ? `${row.requestPreview.propertyAddress?.street}, ${row.requestPreview.propertyAddress?.city || ''} ${row.requestPreview.propertyAddress?.zip || ''}`
                        : (row.skipReason || 'no address')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Live results */}
      {liveResult && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-1">Live run results</h3>
          {typeof liveResult.counts?.apiBatches === 'number' && (
            <p className="text-xs text-gray-500 mb-2">
              {liveResult.counts.apiBatches} BatchData API batch call{liveResult.counts.apiBatches === 1 ? '' : 's'} used.
            </p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <StatBox label="Processed" value={liveResult.counts?.processed ?? 0} />
            <StatBox label="Recovered" value={liveResult.counts?.recovered ?? 0} color="text-green-600" />
            <StatBox label="Unresolved" value={liveResult.counts?.unresolved ?? 0} color="text-orange-600" />
            <StatBox label="No contact" value={liveResult.counts?.noContactReturned ?? 0} color="text-gray-600" />
            <StatBox label="Errors" value={liveResult.counts?.errors ?? 0} color="text-red-600" />
            <StatBox label="Credits" value={liveResult.counts?.creditsUsed ?? 0} color="text-blue-600" />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-4">Owner</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Best match</th>
                  <th className="py-2 pr-4">Persons</th>
                  <th className="py-2 pr-4">Credits</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {(liveResult.results || []).map((r) => (
                  <tr key={r.uniqueId} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-mono text-xs">{r.uniqueId}</td>
                    <td className="py-2 pr-4"><StatusBadge status={r.finalStatus} /></td>
                    <td className="py-2 pr-4">
                      {r.bestName ? `${r.bestName} (${r.bestScore})` : '—'}
                    </td>
                    <td className="py-2 pr-4">{r.personCount ?? 0} / {r.selectedCount ?? 0} selected</td>
                    <td className="py-2 pr-4">{r.creditsUsed ?? 0}</td>
                    <td className="py-2 pr-4">
                      <button
                        onClick={() => setContactsFor(r.uniqueId)}
                        className="text-blue-600 hover:text-blue-800 text-xs"
                      >
                        View contacts
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(liveResult.warnings || []).length > 0 && (
            <div className="mt-3 text-xs text-amber-700">
              {liveResult.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}
        </div>
      )}

      {contactsFor && (
        <OwnerContactsModal uniqueId={contactsFor} onClose={() => setContactsFor(null)} />
      )}
    </div>
  );
};

export default BatchDataV3Panel;
